import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import Reveal from "./Reveal";

/**
 * "Método Sapientia" — a dedicated section that showcases the official
 * proprietary methodology diagram (7 phases + governance hub + transversal pillars).
 * Placed inside the Architecture section, right after the six pillars grid.
 */
export default function MetodoSapientia() {
  const { t } = useLang();
  const m = t.metodo;

  return (
    <section
      id="metodo"
      data-testid="metodo-section"
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ background: "var(--sap-navy)" }}
    >
      {/* Subtle grid overlay */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 75%)",
        }}
      />

      <div className="sap-container relative">
        {/* Intro */}
        <div className="max-w-4xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="sap-divider-gold" />
              <span
                className="text-[11px] uppercase tracking-[0.32em]"
                style={{ color: "var(--sap-gold)" }}
              >
                {m.overline}
              </span>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <h2
              className="font-serif text-white leading-[1.06] tracking-tight"
              style={{
                fontSize: "clamp(2.1rem, 4.2vw, 3.8rem)",
                fontWeight: 400,
              }}
            >
              {m.title}
            </h2>
          </Reveal>

          <Reveal delay={200}>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-white/75 max-w-3xl">
              {m.subtitle}
            </p>
          </Reveal>
        </div>

        {/* Image showcase */}
        <Reveal delay={300}>
          <div className="mt-14 md:mt-20 relative">
            {/* Gold decorative corners */}
            <div
              className="absolute -top-3 -left-3 w-14 h-14 border-l border-t z-10 hidden md:block"
              style={{ borderColor: "var(--sap-gold)" }}
            />
            <div
              className="absolute -bottom-3 -right-3 w-14 h-14 border-r border-b z-10 hidden md:block"
              style={{ borderColor: "var(--sap-gold)" }}
            />
            <div
              className="relative bg-[#081b34] p-3 md:p-6"
              style={{ borderRadius: 2 }}
            >
              <picture>
                <source srcSet="/metodo-sapientia.webp" type="image/webp" />
                <img
                  src="/metodo-sapientia-opt.png"
                  alt="Método Sapientia — Arquitetura da Gestão: 7 etapas conectadas por governança e controladoria"
                  className="w-full h-auto object-contain block"
                  loading="lazy"
                />
              </picture>
            </div>
          </div>
        </Reveal>

        {/* Punchy quote below */}
        <Reveal delay={400}>
          <div className="mt-16 md:mt-20 grid lg:grid-cols-12 gap-10 items-center">
            <div
              className="lg:col-span-8 border-l-2 pl-6 md:pl-8"
              style={{ borderColor: "var(--sap-gold)" }}
            >
              <blockquote
                className="font-serif italic text-white leading-[1.2]"
                style={{
                  fontSize: "clamp(1.5rem, 2.8vw, 2.4rem)",
                  fontWeight: 400,
                }}
              >
                “{m.quote}”
              </blockquote>
            </div>
            <div className="lg:col-span-4">
              <div className="flex flex-wrap gap-x-4 gap-y-2 items-center">
                {m.chips.map((c, i) => (
                  <React.Fragment key={i}>
                    <span
                      className="text-[11px] tracking-[0.28em] uppercase font-semibold"
                      style={{ color: "var(--sap-gold)" }}
                    >
                      {c}
                    </span>
                    {i < m.chips.length - 1 && (
                      <span
                        className="inline-block w-1 h-1 rounded-full"
                        style={{ background: "var(--sap-gold)" }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
